import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import EditorJS, { API } from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Table from '@editorjs/table';
import LinkTool from '@editorjs/link';
import Quote from '@editorjs/quote';
import Checklist from '@editorjs/checklist';
import Embed from '@editorjs/embed';
import ImageTool from '@editorjs/image';
import Delimiter from '@editorjs/delimiter';
import Warning from '@editorjs/warning';
import Marker from '@editorjs/marker';
import InlineCode from '@editorjs/inline-code';

import { BreakpointObserver } from '@angular/cdk/layout';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BREAKPOINTS } from '@sparro/app/core/config/constants.config';

import { JournalService } from '@journal/core/api/journal/journal.service';
import { UserService } from '@sparro/app/core/api/user/user.service';
import { ReferenceDialogComponent } from '@journal/app/modules/journal-edit/reference-dialog/reference-dialog.component';
import { ConfirmationDialogComponent } from '@sparro/app/shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';

import { MetaDefinition } from '@angular/platform-browser';
import { MetaService } from '@sparro/app/core/helpers/meta/meta.service';
import { StorageHandlerService } from '@sparro/core/helpers/storage/storage-handler.service';
import { SnackbarService } from '@sparro/core/helpers/toastr/snackbar.service';
import { Blog } from '@sparro/app/core/models/blog.model';
import { User } from '@sparro/app/core/models/user.model';
import { Reference } from '@sparro/app/core/models/blog.model';

export enum LogLevels {
  VERBOSE = 'VERBOSE',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR'
}

class AddReference {
  api: any;
  component: any;

  constructor({ api }) {
    this.api = api;
    this.component = api.component;
  }

  myCustomMethod() {
    JournalService.instance.openReferenceDialog();
  }

  static get toolbox() {
    return {
      title: 'Reference',
      icon: '<i class="icon-references-bold" style="font-size: 18px; width: 14px;"></i>'
    };
  }

  render(): null {
    this.myCustomMethod();
    return null;
  }

  save() {
    return;
  }
}

class AskAI {
  button: any;
  state: boolean;
  api: API;

  static get isInline() {
    return true;
  }

  constructor({ api }) {
    this.api = api;
    this.button = null;
    this.state = false;
  }

  render() {
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.textContent = 'Ask AI';
    this.button.classList.add('ce-inline-tool');
    this.button.classList.add('ce-inline-tool-ai');

    return this.button;
  }

  async surround(range: Range) {
    const currentIndex = this.api.blocks.getCurrentBlockIndex();
    const response = await JournalService.instance.openDialog(range);
    if (response?.operation == 'replace') {
      const selectedText = range.extractContents();
      const node = document.createTextNode(response.completion);
      range.insertNode(node);
      this.api.blocks.insert('paragraph', { text: '' }, null, 0);
      this.api.blocks.delete(0);
    } else if (response?.operation == 'insert_below') {
      this.api.blocks.insert('paragraph', { text: response.completion }, null, currentIndex + 1);
    }
  }

  checkState(range) {
    return;
  }
}

@Component({
  selector: 'app-journal-edit',
  templateUrl: './journal-edit.component.html',
  styleUrls: ['./journal-edit.component.scss'],
  providers: [DatePipe]
})
export class JournalEditComponent implements OnInit {
  blogForm: FormGroup;
  blog: Blog;
  content: any;

  user: User;
  uuid: string;
  isUploading: boolean = false;
  coverImage: string = '';

  loading: boolean = true;
  canPublish: boolean = false;
  next: boolean = false;
  placeholderList: string[] = [
    'God is in the details.',
    'Form ever follows function.',
    'An idea is salvation by imagination.',
    'Less is more.',
    'To create, one must first question everything',
    'Simplicity is the ultimate sophistication.',
    'Achitecture is the reaching out for the truth.',
    'There are 360 degrees, so why stick to one?',
    'I love my job. What would I retire to?',
    'I think you never stop learning.',
    'To fly we have to have resistance.',
    "Why should I feel like I don't belong?",
    'Design can be a powerful tool for good.',
    "You've got to bumble forward into the unknown.",
    'Architecture is about people.',
    'Would they call me a diva if I were a guy?'
  ];
  placeholder: string;

  imageCount: number = 0;
  embedCount: number = 0;
  wordCount: number = 0;
  lastSaved: string;
  mobileView: boolean = false;
  component = this;

  @ViewChild('title') title: ElementRef;
  @ViewChild('editor') editorElement: ElementRef;

  public editor;
  public imageUploadUrl;
  public csrftoken;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public metaService: MetaService,
    private userService: UserService,
    private journalService: JournalService,
    private changeDetectorRef: ChangeDetectorRef,
    public dialog: MatDialog,
    public datePipe: DatePipe,
    public changeDetection: ChangeDetectorRef,
    public breakpointObserver: BreakpointObserver,
    public storageService: StorageHandlerService,
    public snackbarService: SnackbarService
  ) {
    this.journalService.referneceDialog$.subscribe(() => {
      this.addReference();
    });
  }

  ngOnInit(): void {
    this.getAuthUser();
    this.initializeBreakpoints();
    this.placeholder =
      this.placeholderList[Math.floor(Math.random() * this.placeholderList.length)];
    this.uuid = this.route.snapshot.params.uuid;
    this.getBlog(this.uuid);
    this.updateMetaInfo();
    this.checkAiEditPermission();
  }

  checkAiEditPermission() {
    this.journalService.getAiEditPermission().subscribe(
      (res: any) => {
        if (res) {
          this.journalService.setCanEditUsingAi(res.success);
        }
      },
      error => {
        console.log(error);
      }
    );
  }

  initEditor(): void {
    this.editor = new EditorJS({
      minHeight: 200,
      placeholder: this.placeholder,
      holder: this.editorElement.nativeElement,
      readOnly: false,
      logLevel: LogLevels.ERROR,
      tools: {
        /**
         * Each Tool is a Plugin. Pass them via 'class' option with necessary settings {@link docs/tools.md}
         */
        header: {
          class: Header,
          inlineToolbar: ['link'],
          config: {
            placeholder: 'Header'
          },
          shortcut: 'CMD+SHIFT+H'
        },
        /**
         * Or pass class directly without any configuration
         */
        image: {
          class: ImageTool,
          inlineToolbar: true,
          config: {
            uploader: {
              uploadByFile: async file => {
                if (file.size > 15000000) {
                  return {
                    success: 0,
                    message: 'Please upload image less than 15mb'
                  };
                }

                try {
                  const fd = new FormData();
                  fd.append('uuid', this.blog.uuid);
                  fd.append('title', '');
                  fd.append('description', '');
                  fd.append('file', file, file.name);

                  const response = await this.journalService.uploadMedia(fd);
                  return {
                    success: 1,
                    file: {
                      url: response.link
                    }
                  };
                } catch (error) {
                  return {
                    success: 0,
                    message: error.message
                  };
                }
              },
              uploadByUrl: async url => {
                try {
                  if (this.blog.content && !this.blog.body) {
                    return {
                      success: 1,
                      file: {
                        url: url
                      }
                    };
                  }
                  const formData = new FormData();
                  formData.append('url', url);
                  formData.append('uuid', this.blog.uuid);
                  formData.append('title', '');
                  formData.append('description', '');
                  formData.append('uploaded_by', 'url');

                  const response = await this.journalService.uploadMedia(formData);
                  return {
                    success: 1,
                    file: {
                      url: response.link
                    }
                  };
                } catch (error) {
                  return {
                    success: 0,
                    message: error.message
                  };
                }
              }
            }
          }
        },
        list: {
          class: List,
          inlineToolbar: true,
          shortcut: 'CMD+SHIFT+L'
        },
        checklist: {
          class: Checklist,
          inlineToolbar: true
        },
        quote: {
          class: Quote,
          inlineToolbar: true,
          config: {
            quotePlaceholder: 'Enter a quote',
            captionPlaceholder: "Quote's author"
          },
          shortcut: 'CMD+SHIFT+O'
        },
        warning: Warning,
        marker: {
          class: Marker,
          shortcut: 'CMD+SHIFT+M'
        },
        delimiter: Delimiter,
        inlineCode: {
          class: InlineCode,
          shortcut: 'CMD+SHIFT+C'
        },
        linkTool: LinkTool,
        embed: {
          class: Embed,
          inlineToolbar: true
        },
        table: {
          class: Table,
          inlineToolbar: true,
          shortcut: 'CMD+ALT+T'
        },
        addReference: {
          class: AddReference
        },
        askAi: {
          class: AskAI,
          inlineToolbar: true
        }
      },
      data: this.blog.body ? JSON.parse(this.blog.body) : {},
      onChange: () => {
        this.updateBlog();
      }
    });
    setTimeout(() => {
      if (this.blog.content && !this.blog.body) {
        this.editor.blocks.renderFromHTML(this.blog.content);
      } else {
        this.editor.body = this.blog.body ? this.blog.body : {};
      }
    }, 1000);
  }

  updateBlog(): void {
    this.editor.save().then(data => {
      const payload = {
        author: this.user.id,
        body: JSON.stringify(data)
      };
      this.journalService.updateBlog(this.blog.uuid, payload).subscribe(
        (response: Blog) => {
          this.blog = response;
          this.checkNext();
          this.getFilesCount();
        },
        err => {
          console.log(err);
        }
      );
    });
  }

  getAuthUser(): void {
    this.userService.userInitialized.subscribe((initialized: boolean) => {
      if (initialized) {
        this.userService.user.subscribe((response: User) => {
          this.user = response;
        });
      }
    });
  }

  initializeBreakpoints(): void {
    this.breakpointObserver.observe([BREAKPOINTS.below.md]).subscribe(result => {
      if (result.breakpoints[BREAKPOINTS.below.md] !== this.mobileView) {
        this.mobileView = !this.mobileView;
      }
    });
  }

  updateMetaInfo(): void {
    let metaInfo: MetaDefinition[] = [];
    const title = 'Journal Editor';
    const description = `Publish your own journal, blog, & news in architecture & design.`;
    const image = this.user?.avatar_url ? this.user.avatar_url : '';

    metaInfo = [
      { name: 'title', content: title },
      { name: 'description', content: description },
      { name: 'url', content: window.location.href },
      { name: 'image', content: image },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:url', content: window.location.href },
      { name: 'twitter:image', content: image },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: window.location.href },
      { property: 'og:image', content: image },
      { property: 'og:image:alt', content: title },
      { property: 'og:type', content: 'website' }
    ];
    metaInfo.forEach(meta => {
      this.metaService.updateMetaInfo(meta);
    });
    this.metaService.updateMeta(metaInfo);
    this.metaService.updateTitle(title);
    this.metaService.createCanonicalURL();
  }

  getBlog(uuid: string): void {
    this.journalService.getBlog(uuid).subscribe(
      (response: Blog) => {
        this.blog = response;
        this.initializeForm();
        this.getFilesCount();
        this.timeSinceCounter();
        this.checkNext();
      },
      err => {
        console.log(err);
        this.loading = false;
        this.router.navigate(['error'], {
          queryParams: { code: '404' }
        });
      }
    );
  }

  /*Added the ngModel maxlength to title and tagline in Html to limit the words/characters at live.
  the validation part will validate after publish or after submit*/

  initializeForm(): void {
    this.blogForm = new FormGroup({
      title: new FormControl(this.blog.title, {
        validators: [Validators.required, Validators.maxLength(200)]
      }),
      tagline: new FormControl(this.blog.tagline, {
        validators: [Validators.required, Validators.maxLength(200)]
      })
    });
    this.loading = false;
    setTimeout(() => {
      this.initEditor();
    }, 1000);
    this.blogForm.valueChanges
      .pipe(debounceTime(3000), distinctUntilChanged())
      .subscribe((response: Blog) => {
        const data = {
          author: this.user.id,
          title: response.title,
          tagline: response.tagline
        };

        this.journalService.updateBlog(this.blog.uuid, data).subscribe(
          (response: Blog) => {
            this.blog = response;
            this.checkNext();
            this.getFilesCount();
          },
          err => {
            console.log(err);
          }
        );
      });
  }

  getCount(content: string): void {
    if (content) {
      // var regex = /<img.*?>|<iframe.*?>/g;
      content = content.replace(/<[^>]*>/g, ' ');
      this.wordCount = content.trim().split(' ').length;
    } else {
      this.wordCount = 0;
    }
  }

  checkNext(): void {
    this.journalService.setBlog(this.blog);
    this.next = Boolean(
      this.blog.title &&
        this.blog.tagline &&
        (this.blog?.body ? this.blog.body.length : this.blog?.content?.length) > 30
    );
  }

  timeSinceCounter() {
    this.getTimeSince(this.blog.updated_at);
    setTimeout(() => {
      this.timeSinceCounter();
    }, 1000);
  }

  getTimeSince(updatedDate) {
    const now = Date.now();
    updatedDate = new Date(updatedDate);
    const diff = now - updatedDate;
    let updatedAtString = 'a few seconds ago';
    // console.log(now, diff)

    if (diff < 60000) {
      updatedAtString = 'a few seconds ago';
    } else if (diff < 300000) {
      updatedAtString = 'a few minutes ago';
    } else if (diff < 3000000) {
      updatedAtString = Math.ceil(diff / 1000 / 60) + ' minutes ago';
    } else if (diff < 6300000) {
      updatedAtString = 'about an hour ago';
    } else if (diff < 86340000) {
      updatedAtString = Math.ceil(diff / 1000 / 60 / 60) + ' hours ago';
    } else if (diff < 2592000000) {
      updatedAtString = Math.ceil(diff / 1000 / 60 / 60 / 24) + ' days ago';
    } else if (diff < 31104000000) {
      updatedAtString = Math.ceil(diff / 1000 / 60 / 60 / 24 / 30) + ' months ago';
    } else {
      updatedAtString = Math.ceil(diff / 1000 / 60 / 60 / 24 / 30 / 12) + ' years ago';
    }
    this.lastSaved = 'Saved ' + updatedAtString;
  }

  addReference(): void {
    const dialogRef = this.dialog.open(ReferenceDialogComponent, {
      width: '640px',
      data: { uuid: this.blog.uuid }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.blog.references.push(result);
      }
    });
  }

  editReference(referenceObj: Reference): void {
    const referenceDialog = this.dialog.open(ReferenceDialogComponent, {
      width: '640px',
      data: { new: false, uuid: this.blog.uuid, reference: referenceObj }
    });

    referenceDialog.afterClosed().subscribe(result => {
      const referenceResult: Reference = result;
      if (referenceResult) {
        const referenceObj = referenceResult;
        this.blog.references.map((reference, index) => {
          if (reference.id === referenceObj.id) {
            this.blog.references.splice(index, 1, referenceObj);
          }
          this.changeDetectorRef.detectChanges();
        });
      }
    });
  }

  deleteReference(reference_obj: Reference): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: 'Are you sure you want to delete this reference?',
        okayButtonText: 'Yes'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.journalService.deleteReference(this.blog.uuid, reference_obj.id).subscribe(
          (response: Reference) => {
            if (response) {
              this.blog.references = this.blog.references.filter((reference, index) => {
                return reference.id !== reference_obj.id;
              });
            }
          },
          err => {
            console.log(err);
          }
        );
      }
    });
  }

  getFilesCount(): void {
    const content = this.blog.body;
    if (content) {
      const imageRegex = /<img.*?>/g;
      const embedRegex = /<iframe.*?>/g;
      // console.log(content.match(image_regex), content.match(embed_regex), content)
      const imageMatches = content.match(imageRegex);
      const embedMatches = content.match(embedRegex);
      this.imageCount = imageMatches ? imageMatches.length : 0;
      this.embedCount = embedMatches ? embedMatches.length : 0;
    } else {
      this.imageCount = this.embedCount = 0;
    }
  }
}
